import { useMemo, useLayoutEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import GraphContainer from '../../GraphContainer'
import FlowDiagram from './FlowDiagram'

import { generateThemeAttributionData } from './helpers/dataHelpers'
import { useSroiReportCurrencySymbol } from '../../../hooks'
import { useChartExportMode } from '../../../hooks/useChartExportMode'
import { formatNumber } from '../../../../../../helpers/numbers'

import { socialReturnOnInvestmentReportSelector } from '../../../../../../modules/selectors/socialReturnOnInvestment'

import { fullWidthCharts, helpTexts } from '../../../constants'

import useStyles from './styles'

const MappingOfImpactToThemes = () => {
  const classes = useStyles()
  const { t } = useTranslation()

  const report = useSelector(socialReturnOnInvestmentReportSelector)
  const currencySymbol = useSroiReportCurrencySymbol()

  const { isExporting, onExportStart, onExportEnd, onChartReady } = useChartExportMode()
  const hasSignaledRef = useRef(false)

  const themeData = useMemo(() => {
    return generateThemeAttributionData(report)
  }, [report])

  const formatCurrency = value => {
    return `${currencySymbol}${formatNumber(value)}`
  }

  // Signal when SVG has finished rendering for PNG export
  useLayoutEffect(() => {
    if (isExporting && !hasSignaledRef.current && onChartReady && themeData?.length > 0) {
      hasSignaledRef.current = true
      // Wait for browser to paint the SVG using requestAnimationFrame
      // Double RAF ensures the next paint cycle completes
      requestAnimationFrame(() => {
        //  The double requestAnimationFrame is a common pattern:
        //  the first RAF schedules for the next frame,
        //  and the second ensures we're in the frame after that, guaranteeing the paint is complete.
        requestAnimationFrame(() => {
          onChartReady()
        })
      })
    }

    if (!isExporting) {
      hasSignaledRef.current = false
    }
  }, [isExporting, onChartReady, themeData])

  // Don't render chart if there's no data
  if (!themeData || themeData.length === 0) {
    return null
  }

  return (
    <GraphContainer
      id={fullWidthCharts.sroiMappingThemes}
      title={t('Mapping of Impact to Vision 2030')}
      onExportStart={onExportStart}
      onExportEnd={onExportEnd}
      helpText={helpTexts.mappingOfImpactToVision2030}
    >
      <div className={classes.flowContainer}>
        <FlowDiagram themes={themeData} formatCurrency={formatCurrency} isExporting={isExporting} />
      </div>
    </GraphContainer>
  )
}

export default MappingOfImpactToThemes
